import { getCompanyOptionWithPriceSelect } from '@/api/get/config/get-company-options-with-price-select';
import { PrimaryButton } from '@/components/button/button-component';
import { GridContainer } from '@/components/grid/grid-component';
import CreatableSelect from '@/components/react-hook-form-inputs/select-createable/creatable-search-component';
import { RawSelect } from '@/components/react-hook-form-inputs/select-search/select-search-component';
import Tooltip from '@/components/tooltip/tooltip-component';
import OptionModal from '@/pages/company-management/company-options/option-modal';
import PropTypes from 'prop-types';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import ProductOptionVariantModal from './product-option-variant-modal';
import productOptionStyles from './product-options-style';
import { handleAddNewVariant } from './products-utils';

const {
  AddIconWrapper,
  GroupContainer,
  OptionCount,
  OptionSpan,
  IconWrapper,
  StyledCol,
  StyledRow,
  FooterCol,
  AddIcon,
} = productOptionStyles.styledProductOptionForm;

/**
 * Product Option Form
 * @param {object} props props
 * @param {Function} props.onSubmit onSubmit
 * @param {object} props.defaultValues defaultValues
 * @param {string} props.name name
 * @param {object} props.CONTEXT CONTEXT
 * @returns {JSX.Element}
 */
const ProductOptionForm = ({ onSubmit, defaultValues, name, CONTEXT }) => {
  const { id: companyId } = useParams();

  const { values: formValues = {}, index } = defaultValues;

  const [modalAction, setModalAction] = useState({ label: '', id: null });
  const [isOpenAddOptionVariant, setIsOpenAddOptionVariant] = useState(false);
  const [createVariantLoading, setCreateVariantLoading] = useState(false);
  const selectRef = useRef(null);

  const { getValues: getParentValues } = useFormContext();

  const parentValues = getParentValues(name);
  const fieldToUpdate = parentValues[index];

  const defValues = formValues && {
    option_price_link_id: formValues.option_price_link_id,
    dependencies: formValues.dependencies,
  };
  const { options, setOptions } = useContext(CONTEXT);
  const { control, handleSubmit, formState, getValues, setValue, clearErrors } =
    useForm({
      defaultValues: defValues,
    });

  const methods = useFieldArray({
    control,
    name, // unique name for your Field Array
  });

  const { append } = methods;

  const handleCreateOptionModal = () => {
    setModalAction({ label: 'Add', id: null });
  };

  const closeCreateOptionModal = () => {
    setModalAction({ label: '', id: null });
  };

  const refetchOptions = useCallback(
    (newOption) => {
      if (companyId && setOptions) {
        getCompanyOptionWithPriceSelect(companyId).then((response) => {
          setOptions(response.data);

          // Set the newly created option as selected AFTER options are updated
          if (newOption.optionPriceLinkId) {
            setValue('option_price_link_id', newOption.optionPriceLinkId, {
              shouldValidate: true,
              shouldDirty: true,
            });

            // Clear any validation errors
            clearErrors('option_price_link_id');
          }

          // Close the modal
          closeCreateOptionModal();
        });
      }
    },
    [companyId, setOptions, setValue, clearErrors],
  );

  const addNewVariant = async (values) => {
    const optionId = isOpenAddOptionVariant?.values?.option_id;
    const productId = isOpenAddOptionVariant?.values?.product_id;

    const optionPriceLinkId = await handleAddNewVariant({
      values,
      companyId,
      setOptions,
      setLoading: setCreateVariantLoading,
      setIsOpen: setIsOpenAddOptionVariant,
      optionId,
      productId,
      append,
      fieldToUpdate,
    });

    if (optionPriceLinkId) {
      setValue('option_price_link_id', optionPriceLinkId, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Clear any validation errors
      clearErrors('option_price_link_id');
    }
  };

  const selectOption = useMemo(() => {
    const selectOptions = options.map((option) => ({
      option_id: option.id,
      isVariant: option.isVariant,
      label: option.name,
      options: option.prices.map((value) => ({
        label: `${value.part_number} - ${value.name}`,
        value: value.id,
      })),
    }));
    return selectOptions;
  }, [options]);

  const customGroupLabel = useCallback(
    (data) => {
      const optionCount = data?.options?.length || 0;
      const isVariant = data?.isVariant;

      const renderAddIcon = (disabled) => (
        <AddIconWrapper
          role="none"
          onClick={() => {
            if (isVariant) {
              // Close the select dropdown
              selectRef.current?.blur();
              setIsOpenAddOptionVariant({
                values: {
                  option_id: data?.option_id,
                },
              });
            }
          }}
        >
          <AddIcon disabled={disabled} size={20} />
        </AddIconWrapper>
      );

      return (
        <GroupContainer>
          <OptionSpan>{data?.label}</OptionSpan>
          <IconWrapper>
            {isVariant ? (
              renderAddIcon()
            ) : (
              <Tooltip
                key={data?.option_id}
                id={data?.option_id}
                text="This option is not compatible with option variants"
                icon={renderAddIcon(true)}
              />
            )}

            <OptionCount>{optionCount}</OptionCount>
          </IconWrapper>
        </GroupContainer>
      );
    },
    [setIsOpenAddOptionVariant],
  );

  return (
    <GridContainer>
      <StyledRow>
        <StyledCol xs={12}>
          <CreatableSelect
            ref={selectRef}
            header="Select Option"
            placeholder="Select Option"
            formatCreateLabel="Create New Option"
            isGrouped
            groupedOptions={selectOption}
            customGroupLabel={customGroupLabel}
            isClearable
            name="option_price_link_id"
            cy="selProductOption"
            control={control}
            formState={formState}
            required={{
              required: 'This field is required',
            }}
            onCreateOption={() => handleCreateOptionModal()}
            maxMenuHeight={300}
            hideSelectedOptions={false}
            menuPortalTarget={document.getElementById('select-option')}
          />
        </StyledCol>
        <StyledCol xs={12}>
          <RawSelect
            isGrouped
            groupedOptions={selectOption}
            header="Select Dependencies"
            placeholder="Select Option"
            menuPortalTarget={document.getElementById('select-option')}
            isClearable
            isMulti
            name="dependencies"
            control={control}
            formState={formState}
            required={{
              validate: (values) => {
                if (values?.length) {
                  const value = getValues('option_price_link_id');
                  // We need to check if this option has already been added.
                  if (values.some((item) => item === value)) {
                    return 'Cannot have a dependency on the option itself.';
                  }
                }
                return true;
              },
            }}
            closeMenuOnSelect={false}
            maxMenuHeight={300}
          />
        </StyledCol>
      </StyledRow>
      <StyledRow>
        <FooterCol xs={12}>
          <PrimaryButton
            title={defValues ? 'Save Option' : 'Add Option'}
            type="button"
            onClick={handleSubmit(onSubmit)}
          />
        </FooterCol>
      </StyledRow>
      <OptionModal
        modalAction={modalAction}
        closeModal={closeCreateOptionModal}
        bodyStyle={{ width: '100%', padding: 0 }}
        onCreate={refetchOptions}
      />
      <ProductOptionVariantModal
        isOpen={isOpenAddOptionVariant}
        setIsOpen={setIsOpenAddOptionVariant}
        onSubmit={addNewVariant}
        loading={createVariantLoading}
      />
    </GridContainer>
  );
};

ProductOptionForm.defaultProps = {
  defaultValues: { values: {} },
};

ProductOptionForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  CONTEXT: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
};

export default ProductOptionForm;
